import { useState, useEffect, useCallback, useRef } from 'react';
import type { User } from '@supabase/supabase-js';
import { Button, Input, Select } from '@/components/ui';
import {
  getMyFamilyRole,
  setMyFamilyRole,
  updateProfile,
} from '@/services/user.service';
import { profileImageService } from '@/services';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { useUserStore } from '@/stores/userStore';
import { useOnboardingFooter } from './OnboardingFooterContext';
import { ChangeProfileImage } from '@/components/domain/shared/ChangeProfileImage';
import { ProfileAvatar } from '@/components/domain/shared/ProfileAvatar';
import { SelectEmoji } from '@/components/domain/shared/SelectEmoji';
import { ROLE_OPTIONS, CUSTOM_ROLE_SENTINEL } from '@/constants/roles';
import {
  DEFAULT_AVATAR_EMOJI,
  googleAvatarUrlFrom,
  ownAvatarProps,
} from '@/lib/ownAvatar';
import { ONBOARDING_COPY } from './onboardingCopy';

export interface StepProfileSetupProps {
  user: User;
  onComplete: () => void;
  /**
   * The household this profile's family role belongs to (YAR-485): the one
   * the admin just created, or the one the invitee is joining. The role is
   * read from and saved to THAT membership, so a second household starts
   * empty instead of inheriting the first one's answer. Without it (a
   * legacy path where no household exists yet) the role falls back to the
   * account-level users.role.
   */
  householdId?: string | null;
}

type ProfileSubState = 'form' | 'changeImage' | 'selectEmoji';

// Build timezone options once at module load — native Intl, no extra dependency
const TIMEZONE_OPTIONS: { value: string; label: string }[] = (() => {
  try {
    const now = new Date();
    return Intl.supportedValuesOf('timeZone').map((tz) => {
      const offset =
        new Intl.DateTimeFormat('en', {
          timeZone: tz,
          timeZoneName: 'shortOffset',
        })
          .formatToParts(now)
          .find((p) => p.type === 'timeZoneName')?.value ?? '';
      return { value: tz, label: `${tz.replace(/_/g, ' ')} (${offset})` };
    });
  } catch {
    return [];
  }
})();

/** Split a stored role into the Role Select pair (preset vs custom text). */
function splitRole(role: string): { roleSelect: string; customRole: string } {
  if (!role) return { roleSelect: '', customRole: '' };
  return ROLE_OPTIONS.some(
    (o) => o.value !== CUSTOM_ROLE_SENTINEL && o.value === role,
  )
    ? { roleSelect: role, customRole: '' }
    : { roleSelect: CUSTOM_ROLE_SENTINEL, customRole: role };
}

export function StepProfileSetup({
  user,
  onComplete,
  householdId,
}: StepProfileSetupProps) {
  const copy = ONBOARDING_COPY.profile;
  const { setFooter } = useOnboardingFooter();
  const storeTimezone = useOnboardingStore((state) => state.formData.timezone);
  const storedAvatarEmoji = useOnboardingStore(
    (state) => state.formData.avatarEmoji,
  );
  const storedAvatarColor = useOnboardingStore(
    (state) => state.formData.avatarColor,
  );
  const storedRole = useOnboardingStore((state) => state.formData.role);
  const storedDisplayName = useOnboardingStore(
    (state) => state.formData.displayName,
  );
  const setFormData = useOnboardingStore((state) => state.setFormData);
  const dbUserId = useUserStore((state) => state.userId);
  const storedAvatarImagePath = useUserStore((state) => state.avatarImagePath);
  // The avatar belongs to the PERSON (users row), not to the household. The
  // onboarding store is reset for every household, so on a second household's
  // onboarding it is empty — seeding only from it showed the default avatar
  // over the one already saved (YAR-485 follow-up, Yeri Sep 30).
  const savedAvatarEmoji = useUserStore((state) => state.avatarEmoji);
  const savedAvatarColor = useUserStore((state) => state.avatarColor);
  const updateUserStore = useUserStore((state) => state.updateProfile);

  const [subState, setSubState] = useState<ProfileSubState>('form');
  const [avatarEmoji, setAvatarEmoji] = useState(
    storedAvatarEmoji ||
      savedAvatarEmoji ||
      user.user_metadata?.avatar_emoji ||
      DEFAULT_AVATAR_EMOJI,
  );
  const [avatarColor, setAvatarColor] = useState(
    storedAvatarColor || savedAvatarColor || 'bg-emoji-1',
  );
  const [avatarImagePath, setAvatarImagePath] = useState<string | null>(
    storedAvatarImagePath ?? null,
  );
  // The Google photo is the DEFAULT avatar (see lib/ownAvatar). It used to be
  // shown here only, while `avatar_emoji` was written unconditionally on
  // Continue — so the photo appeared during setup and then vanished on the
  // dashboard, replaced by the unicorn that had just been stamped onto the
  // row. `emojiChosen` is what fixes that: unless the parent actually saves
  // from the emoji picker, we leave `avatar_emoji` NULL and every surface
  // resolves to the Google photo.
  const googleAvatarUrl = googleAvatarUrlFrom(user);
  // A saved emoji counts as chosen: it must render (not fall back to the
  // Google photo or the default) and is written back unchanged on Continue.
  const [emojiChosen, setEmojiChosen] = useState(
    Boolean(storedAvatarEmoji || savedAvatarEmoji),
  );
  const [displayName, setDisplayName] = useState(
    storedDisplayName ||
      user.user_metadata?.display_name ||
      user.user_metadata?.name ||
      '',
  );
  // With a household, the role comes from that membership (loaded below),
  // never from this device's stored answer: that one may be another
  // household's (YAR-485).
  const initialRole = householdId ? '' : storedRole;
  const [roleSelect, setRoleSelect] = useState(
    () => splitRole(initialRole).roleSelect,
  );
  const [customRole, setCustomRole] = useState(
    () => splitRole(initialRole).customRole,
  );
  // Set once the parent picks or types a role, so a late membership read
  // never overwrites what they chose.
  const roleTouched = useRef(false);

  useEffect(() => {
    if (!householdId) return;
    let cancelled = false;
    getMyFamilyRole(householdId)
      .then((role) => {
        if (cancelled || roleTouched.current) return;
        const split = splitRole(role ?? '');
        setRoleSelect(split.roleSelect);
        setCustomRole(split.customRole);
      })
      .catch(() => {
        /* the field stays empty; the parent picks a role */
      });
    return () => {
      cancelled = true;
    };
  }, [householdId]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolvedRole =
    roleSelect === CUSTOM_ROLE_SENTINEL ? customRole.trim() : roleSelect;

  // AC6 (locked V2 behavior): Continue stays disabled until every field has
  // a value — name, role (custom text counts only once non-blank, via
  // resolvedRole), and timezone.
  const canSubmit =
    displayName.trim().length > 0 &&
    resolvedRole.length > 0 &&
    storeTimezone.length > 0;

  const handleTimezoneChange = useCallback(
    (e: { target: { value: string } }) => {
      setFormData({ timezone: e.target.value });
    },
    [setFormData],
  );

  const handleComplete = useCallback(async () => {
    if (!canSubmit) return;

    setSaving(true);
    setError(null);

    try {
      // avatar_emoji / avatar_color go in ONLY when the parent saved from the
      // emoji picker. Writing them unconditionally stamped the placeholder
      // unicorn onto the row and permanently outranked the Google photo — the
      // same guard Settings → Profile already applies via `avatarTouched`.
      await updateProfile({
        display_name: displayName.trim(),
        timezone: storeTimezone || undefined,
        ...(emojiChosen
          ? { avatar_emoji: avatarEmoji, avatar_color: avatarColor }
          : {}),
        // Legacy path only: no household to hold the role yet.
        ...(!householdId && resolvedRole ? { role: resolvedRole } : {}),
      });
      if (householdId) {
        await setMyFamilyRole(householdId, resolvedRole);
        useUserStore.getState().updateProfile({ profileRole: resolvedRole });
      }
      setFormData({
        displayName: displayName.trim(),
        ...(emojiChosen ? { avatarEmoji, avatarColor } : {}),
        timezone: storeTimezone,
        role: resolvedRole,
      });
      updateUserStore({
        ...(emojiChosen ? { avatarEmoji, avatarColor } : {}),
      });
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  }, [
    canSubmit,
    displayName,
    avatarEmoji,
    avatarColor,
    emojiChosen,
    storeTimezone,
    resolvedRole,
    householdId,
    setFormData,
    updateUserStore,
    onComplete,
  ]);

  useEffect(() => {
    setFooter(
      <Button
        variant="primary"
        fullWidth
        disabled={!canSubmit}
        loading={saving}
        onClick={handleComplete}
      >
        {copy.cta}
      </Button>,
    );
    return () => setFooter(null);
  }, [canSubmit, saving, handleComplete, setFooter, copy.cta]);

  const handleEmojiSave = (emoji: string, color: string) => {
    setAvatarEmoji(emoji);
    setAvatarColor(color);
    // The explicit override — from here on the emoji outranks the Google photo.
    setEmojiChosen(true);
    setSubState('form');
  };

  const handleUploadImage = useCallback(
    async (file: File) => {
      if (!dbUserId) throw new Error('User not loaded yet');
      const { avatar_image_path } = await profileImageService.uploadAvatarImage(
        'user',
        dbUserId,
        file,
      );
      setAvatarImagePath(avatar_image_path);
      updateUserStore({ avatarImagePath: avatar_image_path });
      setSubState('form');
    },
    [dbUserId, updateUserStore],
  );

  const handleRemoveImage = useCallback(async () => {
    if (!dbUserId) throw new Error('User not loaded yet');
    await profileImageService.removeAvatarImage('user', dbUserId);
    setAvatarImagePath(null);
    updateUserStore({ avatarImagePath: null });
    setSubState('form');
  }, [dbUserId, updateUserStore]);

  // ── Sub-state overlays ───────────────────────────────────────────────────
  if (subState === 'changeImage') {
    return (
      <ChangeProfileImage
        onBack={() => setSubState('form')}
        onChooseEmoji={() => setSubState('selectEmoji')}
        onUpload={dbUserId ? handleUploadImage : undefined}
        onRemove={dbUserId && avatarImagePath ? handleRemoveImage : undefined}
        hasImage={!!avatarImagePath}
        // Resolve through ownAvatarProps, exactly as the avatar on the form
        // behind this sheet does (YAR-302). Hand-building the preview from
        // the stored fields alone dropped the Google photo — which lives on
        // the session, not in `avatar_image_path` — so a user whose avatar
        // came from Google saw their face on the form and the default
        // unicorn the moment they tapped it.
        preview={{
          ...ownAvatarProps({
            avatarImagePath,
            avatarEmoji: emojiChosen ? avatarEmoji : null,
            googleAvatarUrl,
          }),
          color: avatarColor,
        }}
      />
    );
  }

  if (subState === 'selectEmoji') {
    return (
      <SelectEmoji
        currentEmoji={avatarEmoji}
        currentColor={avatarColor}
        onSave={handleEmojiSave}
        onBack={() => setSubState('changeImage')}
      />
    );
  }

  // ── Main form ────────────────────────────────────────────────────────────
  return (
    <>
      <div className="flex flex-col gap-8">
        {/* Title — title-only per locked copy (no subtitle). */}
        <h1 className="text-[28px] leading-8 font-bold text-text-hero">
          {copy.title}
        </h1>

        {/* Avatar section */}
        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={() => setSubState('changeImage')}
            className="cursor-pointer rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            aria-label="Change profile image"
          >
            <ProfileAvatar
              {...ownAvatarProps({
                avatarImagePath,
                avatarEmoji: emojiChosen ? avatarEmoji : null,
                googleAvatarUrl,
              })}
              color={avatarColor}
              size="2xl"
            />
          </button>
          <span className="text-sm font-medium text-muted-foreground">
            {copy.avatarCaption}
          </span>
        </div>

        {/* Form fields */}
        <div className="flex flex-col gap-5">
          <Input
            label={copy.nameLabel}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={copy.namePlaceholder}
            required
          />

          {/* Role selector */}
          <div className="flex flex-col gap-1.5">
            {/* The profile frame binds this placeholder to
                `text/content/placeholder` (#7a7a7a) — unlike the invite
                screen's Access Level trigger, which QA verified as body copy
                (YAR-159). Opt in per screen; see `placeholderTone`. */}
            <Select
              label={copy.roleLabel}
              placeholder={copy.rolePlaceholder}
              placeholderTone="placeholder"
              options={ROLE_OPTIONS}
              value={roleSelect}
              onChange={(e) => {
                roleTouched.current = true;
                setRoleSelect(e.target.value);
              }}
            />
            {roleSelect === CUSTOM_ROLE_SENTINEL && (
              <Input
                label="Your role"
                value={customRole}
                onChange={(e) => {
                  roleTouched.current = true;
                  setCustomRole(e.target.value);
                }}
                placeholder="e.g. Grandma, Uncle..."
              />
            )}
          </div>

          {/* Timezone selector */}
          <Select
            label={copy.timezoneLabel}
            placeholder={copy.timezonePlaceholder}
            options={TIMEZONE_OPTIONS}
            value={storeTimezone}
            onChange={handleTimezoneChange}
          />
        </div>

        {error && (
          <p
            className="text-sm text-text-error"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    </>
  );
}
